import {
  Component,
  effect,
  ElementRef,
  input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { IOHAppService } from 'src/app/services/ioh-app.service';
import { IOHMapService } from 'src/app/services/ioh-map.service';
import { IOHMapServiceIconsComponent } from '../ioh-map-service-icons/ioh-map-service-icons.component';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'ioh-map',
  templateUrl: './ioh-map.component.html',
  styleUrls: ['./ioh-map.component.scss'],
  imports: [CommonModule, IOHMapServiceIconsComponent, ToastModule]
})
export class IOHMapComponent implements OnInit, OnDestroy {
  zoomIohValue: any = 4;
  iohShowFilter = input(false);
  queryParams = input<any>([]);
  routeQueryParams = null;
  private destroy$ = new Subject<void>();
  private resizeAnimationFrameId: number | null = null;
  private mapInitialized = false;
  iohLevelLabel = input<string>('');
  @ViewChild('iohMapWrapper', { static: true }) mapWrapperRef?: ElementRef<HTMLElement>;
  constructor(
    public iohMapService: IOHMapService,
    public iohAppService: IOHAppService,
    private route: ActivatedRoute
  ) {
    // Subscription for route params (not a signal)
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.routeQueryParams = params;
      this.zoomToRegionOrZone(params);
    });

    effect(() => {
      const slot = this.iohMapService.activeMapSlot();
      if (slot && !this.mapInitialized) {
        this.mapInitialized = true;
        this.iohMapService
          .InitalizeIohMap(this.mapWrapperRef?.nativeElement)
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      }
    });

    // Effects for signals
    effect(() => {
      const gisInfo = this.iohAppService.iohGisDataLoaded$();
      if (gisInfo.gisRegionsLoaded && gisInfo.gisZonesLoaded) {
        this.zoomToRegionOrZone(this.routeQueryParams);
      }
    });

    effect(() => {
      const trains = this.iohAppService.iohl3Trains$();
      const trainsGeoJson = {
        features: [],
        type: 'FeatureCollection'
      };
      trains?.forEach(train => {
        const feature: any = {};
        feature.type = 'Feature';
        feature.geometry = {
          type: 'Point',
          coordinates: [train.longitude, train.latitude]
        };
        feature.properties = {
          ...train
        };
        feature.properties.statusColor = this.iohMapService.iohStatusColor(
          feature.properties.status
        );
        const trainNameParts = this.iohMapService.iohFormatTrainName(
          feature.properties.trainId
        );
        feature.properties.firstPart = trainNameParts.firstPart;
        feature.properties.middlePart = trainNameParts.middlePart;
        feature.properties.lastPart = trainNameParts.lastPart;
        trainsGeoJson.features.push(feature);
      });
      this.iohMapService.trainsForMap = JSON.parse(
        JSON.stringify(trainsGeoJson)
      );
      this.iohMapService.hideIohSpecificLayers();
      if (this.iohLevelLabel() === 'level-3') {
        this.iohMapService.displayIohL3Data();
      }
    });
  }

  ngOnInit(): void {
    setTimeout(() => {
      if (!this.mapInitialized) {
        this.mapInitialized = true;
        this.iohMapService
          .InitalizeIohMap(this.mapWrapperRef?.nativeElement)
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      }
    }, 3000);
  }

  zoomToRegionOrZone(queryParams) {
    if (queryParams && queryParams.regionCode) {
      const regionsBBox: any[] = JSON.parse(
        sessionStorage.getItem('regionsbbox')
      );
      const currentRegonBBox = regionsBBox.find(regionElement => {
        return regionElement.regionCode === queryParams.regionCode;
      });
      setTimeout(() => {
        this.iohMapService.zoomToSpecificBoundingBox([
          [currentRegonBBox.x1, currentRegonBBox.y1],
          [currentRegonBBox.x2, currentRegonBBox.y2]
        ]);
      }, 100);
    } else if (queryParams && queryParams.zoneCode) {
      const zoneBBox: any[] = JSON.parse(sessionStorage.getItem('zonebbox'));
      const currentZoneBBox = zoneBBox.find(regionElement => {
        return regionElement.zoneCode === queryParams.zoneCode;
      });
      setTimeout(() => {
        this.iohMapService.zoomToSpecificBoundingBox([
          [currentZoneBBox.x1, currentZoneBBox.y1],
          [currentZoneBBox.x2, currentZoneBBox.y2]
        ]);
      }, 100);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes &&
      changes.iohShowFilter &&
      changes.iohShowFilter.currentValue !== changes.iohShowFilter.previousValue
    ) {
      this.scheduleMapResize();
    }
    this.iohMapService.hideIohSpecificLayers();
    if (this.iohLevelLabel() === 'level-3') {
      this.iohMapService.displayIohL3Data();
    }
  }

  private scheduleMapResize(): void {
    if (!this.iohMapService?.map) {
      return;
    }

    if (this.resizeAnimationFrameId !== null) {
      cancelAnimationFrame(this.resizeAnimationFrameId);
    }

    this.resizeAnimationFrameId = requestAnimationFrame(() => {
      this.resizeAnimationFrameId = requestAnimationFrame(() => {
        this.iohMapService.map?.resize();
        this.resizeAnimationFrameId = null;
      });
    });
  }

  ngOnDestroy(): void {
    if (this.resizeAnimationFrameId !== null) {
      cancelAnimationFrame(this.resizeAnimationFrameId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
